/**
 * Viewpoint search-planning module.
 * Expands one requested preset into an ordered ladder of nearby alternatives that
 * downstream scorers can evaluate before the final planner picks a winner.
 */
import type { ViewpointBounds, ViewpointPreset, ViewpointVector3 } from '../generate-jig.types'
import { VIEWPOINT_PRESETS, VIEWPOINT_DISTANCE_MULTIPLIERS } from './viewpoint-presets'
import type { ViewCandidate, ViewpointFamily } from './view-decision-types'
import type { ViewpointPolicyOverrides } from './viewpoint-policy'
import { resolveViewpointPolicy } from './viewpoint-policy'
import {
  add,
  buildTargetPosition,
  computeAabbFramedDistance,
  diagonal,
  directionToYawPitch,
  normalize,
  scale,
  sub,
  yawPitchToDirection,
} from './viewpoint-math'

interface PlannerOptions {
  sceneBounds: ViewpointBounds
  targetBounds: ViewpointBounds
  requestedPreset: ViewpointPreset
  explicitDistance?: number
  targetY: number
  policy?: ViewpointPolicyOverrides
}

const VIEW_SEARCH_FOV_DEG = 60
const VIEW_SEARCH_ASPECT = 16 / 9
const VIEW_SEARCH_FRAME_FILL = 0.9
const VIEW_DISTANCE_BUFFER = 1.15

/**
 * Maps presentation presets to the broader directional family they belong to.
 * This lets the planner preserve the requested "feel" while still testing nearby
 * alternatives such as oblique, lift, or outward-biased variants.
 */
export const VIEWPOINT_FAMILIES: Record<ViewpointPreset, ViewpointFamily> = {
  front: 'front',
  back: 'back',
  top: 'top',
  side: 'side',
  left: 'left',
  'close-up': 'front',
  wide: 'front',
  'low-angle': 'front',
  'high-angle': 'front',
  'rear-high-angle': 'back',
}

const FAMILY_BASE_PRESETS: Record<ViewpointFamily, ViewpointPreset> = {
  front: 'front',
  back: 'back',
  top: 'top',
  side: 'side',
  left: 'left',
}

const OPPOSITE_FAMILY: Record<ViewpointFamily, ViewpointFamily> = {
  front: 'back',
  back: 'front',
  top: 'front',
  side: 'left',
  left: 'side',
}

const ORTHOGONAL_FAMILIES: Record<ViewpointFamily, ViewpointFamily[]> = {
  front: ['side', 'left'],
  back: ['side', 'left'],
  top: ['front', 'side'],
  side: ['front', 'back'],
  left: ['front', 'back'],
}

function isFramingModifierPreset(preset: ViewpointPreset): boolean {
  return preset === 'close-up' || preset === 'wide'
}

/**
 * Returns the canonical family for a requested preset.
 */
export function getViewpointFamily(preset: ViewpointPreset): ViewpointFamily {
  return VIEWPOINT_FAMILIES[preset]
}

function getFamilyBasePreset(family: ViewpointFamily): ViewpointPreset {
  return FAMILY_BASE_PRESETS[family]
}

function getOppositeFamily(family: ViewpointFamily): ViewpointFamily {
  return OPPOSITE_FAMILY[family]
}

function getOrthogonalFamilies(family: ViewpointFamily): ViewpointFamily[] {
  return ORTHOGONAL_FAMILIES[family]
}

function getDistancePreset(requestedPreset: ViewpointPreset, selectedPreset: ViewpointPreset): ViewpointPreset {
  return isFramingModifierPreset(requestedPreset) ? requestedPreset : selectedPreset
}

function computeCandidateDistance(
  targetBounds: ViewpointBounds,
  direction: ViewpointVector3,
  distancePreset: ViewpointPreset,
  explicitDistance: number | undefined
): number {
  if (typeof explicitDistance === 'number' && isFinite(explicitDistance) && explicitDistance > 0) {
    return explicitDistance
  }

  const minDistance = VIEWPOINT_PRESETS[distancePreset].distance
  const diagonalDistance = Math.max(diagonal(targetBounds.size) * VIEWPOINT_DISTANCE_MULTIPLIERS[distancePreset], minDistance)
  const framedDistance = computeAabbFramedDistance(targetBounds, direction, minDistance, {
    fovDeg: VIEW_SEARCH_FOV_DEG,
    aspect: VIEW_SEARCH_ASPECT,
    frameFill: VIEW_SEARCH_FRAME_FILL,
  })

  return Math.max(diagonalDistance, framedDistance) * VIEW_DISTANCE_BUFFER
}

function createCandidate(params: {
  sourceSeed: ViewCandidate['sourceSeed']
  sourcePreset: ViewpointPreset
  selectedFamily: ViewpointFamily
  directionLabel: string
  direction: ViewpointVector3
  requestedDirection: ViewpointVector3
  targetBounds: ViewpointBounds
  explicitDistance?: number
  targetY: number
  liftOffset?: number
  distancePreset?: ViewpointPreset
}): ViewCandidate {
  const normalizedDirection = normalize(params.direction)
  const distancePreset = params.distancePreset || params.sourcePreset
  const liftOffset = typeof params.liftOffset === 'number' ? params.liftOffset : 0
  const distance = computeCandidateDistance(params.targetBounds, normalizedDirection, distancePreset, params.explicitDistance)
  const targetPosition = buildTargetPosition(params.targetBounds, params.targetY)

  return {
    sourceSeed: params.sourceSeed,
    sourcePreset: params.sourcePreset,
    selectedFamily: params.selectedFamily,
    directionLabel: params.directionLabel,
    direction: normalizedDirection,
    requestedDirection: normalize(params.requestedDirection),
    targetBounds: params.targetBounds,
    targetPosition,
    distance,
    liftOffset,
    cameraPosition: add(
      add(targetPosition, scale(normalizedDirection, distance)),
      { x: 0, y: liftOffset, z: 0 }
    ),
  }
}

function dedupeCandidates(candidates: ViewCandidate[]): ViewCandidate[] {
  const seen: Record<string, boolean> = {}

  return candidates.filter(function (candidate) {
    const key = [
      candidate.direction.x.toFixed(4),
      candidate.direction.y.toFixed(4),
      candidate.direction.z.toFixed(4),
      candidate.liftOffset.toFixed(4),
    ].join(':')

    if (seen[key]) {
      return false
    }

    seen[key] = true
    return true
  })
}

function getNudgeBiasFamily(
  sceneBounds: ViewpointBounds,
  targetBounds: ViewpointBounds,
  requestedFamily: ViewpointFamily,
  policyOverrides?: ViewpointPolicyOverrides
): ViewpointFamily | null {
  const policy = resolveViewpointPolicy(policyOverrides)
  const offset = sub(targetBounds.center, sceneBounds.center)
  const halfSizeX = Math.max(sceneBounds.size.x / 2, 1e-6)
  const halfSizeZ = Math.max(sceneBounds.size.z / 2, 1e-6)
  const normalizedX = offset.x / halfSizeX
  const normalizedZ = offset.z / halfSizeZ

  if (requestedFamily === 'front' || requestedFamily === 'back') {
    if (Math.abs(normalizedX) < policy.search.nudgeEscapeThreshold) {
      return null
    }

    return normalizedX >= 0 ? 'side' : 'left'
  }

  if (requestedFamily === 'side' || requestedFamily === 'left') {
    if (Math.abs(normalizedZ) < policy.search.nudgeEscapeThreshold) {
      return null
    }

    return normalizedZ >= 0 ? 'front' : 'back'
  }

  return null
}

function buildSameFamilyNudgeCandidate(options: PlannerOptions, requestedDirection: ViewpointVector3): ViewCandidate | null {
  const family = getViewpointFamily(options.requestedPreset)
  if (family === 'top') {
    return null
  }

  const biasFamily = getNudgeBiasFamily(options.sceneBounds, options.targetBounds, family, options.policy)
  if (!biasFamily) {
    return null
  }

  const biasPreset = getFamilyBasePreset(biasFamily)

  return createCandidate({
    sourceSeed: 'same-family-nudge',
    sourcePreset: getFamilyBasePreset(family),
    selectedFamily: family,
    directionLabel: family + '-nudge-' + biasFamily,
    direction: add(requestedDirection, normalize(VIEWPOINT_PRESETS[biasPreset].position)),
    requestedDirection: requestedDirection,
    targetBounds: options.targetBounds,
    explicitDistance: options.explicitDistance,
    targetY: options.targetY,
    distancePreset: getDistancePreset(options.requestedPreset, getFamilyBasePreset(family)),
  })
}

function buildSameFamilyObliqueCandidates(options: PlannerOptions, requestedDirection: ViewpointVector3): ViewCandidate[] {
  const family = getViewpointFamily(options.requestedPreset)
  if (family === 'top') {
    return []
  }

  if (options.requestedPreset !== 'front' && options.requestedPreset !== 'back' && !isFramingModifierPreset(options.requestedPreset)) {
    return []
  }

  return getOrthogonalFamilies(family).map(function (biasFamily: ViewpointFamily) {
    const biasPreset = getFamilyBasePreset(biasFamily)

    return createCandidate({
      sourceSeed: 'same-family-oblique',
      sourcePreset: getFamilyBasePreset(family),
      selectedFamily: family,
      directionLabel: family + '-oblique-' + biasFamily,
      direction: add(requestedDirection, normalize(VIEWPOINT_PRESETS[biasPreset].position)),
      requestedDirection: requestedDirection,
      targetBounds: options.targetBounds,
      explicitDistance: options.explicitDistance,
      targetY: options.targetY,
      distancePreset: getDistancePreset(options.requestedPreset, getFamilyBasePreset(family)),
    })
  })
}

function buildMicroYawCandidates(options: PlannerOptions, requestedDirection: ViewpointVector3): ViewCandidate[] {
  const family = getViewpointFamily(options.requestedPreset)
  if (family === 'top') {
    return []
  }

  const angles = directionToYawPitch(requestedDirection)
  const policy = resolveViewpointPolicy(options.policy)

  return policy.search.microYawOffsetsDeg.map(function (offsetDeg: number) {
    return createCandidate({
      sourceSeed: 'micro-yaw',
      sourcePreset: options.requestedPreset,
      selectedFamily: family,
      directionLabel: options.requestedPreset + '-micro-yaw' + (offsetDeg >= 0 ? '+' : '') + offsetDeg,
      direction: yawPitchToDirection(angles.yawDeg + offsetDeg, angles.pitchDeg),
      requestedDirection: requestedDirection,
      targetBounds: options.targetBounds,
      explicitDistance: options.explicitDistance,
      targetY: options.targetY,
      distancePreset: options.requestedPreset,
    })
  })
}

function buildSameFamilyLiftCandidate(options: PlannerOptions, requestedDirection: ViewpointVector3): ViewCandidate | null {
  const family = getViewpointFamily(options.requestedPreset)
  if (family === 'top') {
    return null
  }

  const liftOffset = Math.max(options.sceneBounds.size.y * 0.25, options.targetBounds.size.y * 0.25)

  return createCandidate({
    sourceSeed: 'same-family-lift',
    sourcePreset: getFamilyBasePreset(family),
    selectedFamily: family,
    directionLabel: family + '-lift',
    direction: requestedDirection,
    requestedDirection: requestedDirection,
    targetBounds: options.targetBounds,
    explicitDistance: options.explicitDistance,
    targetY: options.targetY,
    liftOffset: liftOffset,
    distancePreset: getDistancePreset(options.requestedPreset, getFamilyBasePreset(family)),
  })
}

function buildOrthogonalCandidates(options: PlannerOptions, requestedDirection: ViewpointVector3): ViewCandidate[] {
  const family = getViewpointFamily(options.requestedPreset)

  return getOrthogonalFamilies(family).map(function (orthogonalFamily: ViewpointFamily) {
    const preset = getFamilyBasePreset(orthogonalFamily)

    return createCandidate({
      sourceSeed: orthogonalFamily === 'top' ? 'top' : 'orthogonal',
      sourcePreset: preset,
      selectedFamily: orthogonalFamily,
      directionLabel: 'orthogonal-' + orthogonalFamily,
      direction: VIEWPOINT_PRESETS[preset].position,
      requestedDirection: requestedDirection,
      targetBounds: options.targetBounds,
      explicitDistance: options.explicitDistance,
      targetY: options.targetY,
      distancePreset: getDistancePreset(options.requestedPreset, preset),
    })
  })
}

/**
 * Chooses a family that "looks in" from the side of the scene where the target sits.
 * This is mainly used for edge-positioned targets that read poorly from a centered
 * front/back framing.
 */
export function getOutwardFamilyForTarget(
  sceneBounds: ViewpointBounds,
  targetBounds: ViewpointBounds,
  policyOverrides?: ViewpointPolicyOverrides
): ViewpointFamily | null {
  const policy = resolveViewpointPolicy(policyOverrides)
  const offset = sub(targetBounds.center, sceneBounds.center)
  const halfSizeX = Math.max(sceneBounds.size.x / 2, 1e-6)
  const halfSizeZ = Math.max(sceneBounds.size.z / 2, 1e-6)
  const normalizedX = Math.abs(offset.x) / halfSizeX
  const normalizedZ = Math.abs(offset.z) / halfSizeZ

  if (Math.max(normalizedX, normalizedZ) < policy.search.outwardEscapeThreshold) {
    return null
  }

  if (normalizedX >= normalizedZ) {
    return offset.x >= 0 ? 'side' : 'left'
  }

  return offset.z >= 0 ? 'front' : 'back'
}

function buildOutwardCandidate(options: PlannerOptions, requestedDirection: ViewpointVector3): ViewCandidate | null {
  const family = getOutwardFamilyForTarget(options.sceneBounds, options.targetBounds, options.policy)
  if (!family) {
    return null
  }

  const preset = getFamilyBasePreset(family)

  return createCandidate({
    sourceSeed: 'outward',
    sourcePreset: preset,
    selectedFamily: family,
    directionLabel: 'outward-' + family,
    direction: VIEWPOINT_PRESETS[preset].position,
    requestedDirection: requestedDirection,
    targetBounds: options.targetBounds,
    explicitDistance: options.explicitDistance,
    targetY: options.targetY,
    distancePreset: getDistancePreset(options.requestedPreset, preset),
  })
}

function buildOppositeCandidate(options: PlannerOptions, requestedDirection: ViewpointVector3): ViewCandidate {
  const family = getOppositeFamily(getViewpointFamily(options.requestedPreset))
  const preset = getFamilyBasePreset(family)

  return createCandidate({
    sourceSeed: 'opposite',
    sourcePreset: preset,
    selectedFamily: family,
    directionLabel: 'opposite-' + family,
    direction: VIEWPOINT_PRESETS[preset].position,
    requestedDirection: requestedDirection,
    targetBounds: options.targetBounds,
    explicitDistance: options.explicitDistance,
    targetY: options.targetY,
    distancePreset: getDistancePreset(options.requestedPreset, preset),
  })
}

function buildTopCandidate(options: PlannerOptions, requestedDirection: ViewpointVector3): ViewCandidate {
  return createCandidate({
    sourceSeed: 'top',
    sourcePreset: 'top',
    selectedFamily: 'top',
    directionLabel: 'top',
    direction: VIEWPOINT_PRESETS.top.position,
    requestedDirection: requestedDirection,
    targetBounds: options.targetBounds,
    explicitDistance: options.explicitDistance,
    targetY: options.targetY,
    distancePreset: 'top',
  })
}

/**
 * Builds the ordered candidate ladder evaluated by `planViewpoint`.
 * The requested view is always first, followed by progressively more invasive
 * fallbacks that trade fidelity for visibility.
 */
export function buildViewCandidateLadder(options: PlannerOptions): ViewCandidate[] {
  const requestedDirection = normalize(VIEWPOINT_PRESETS[options.requestedPreset].position)
  const requestedFamily = getViewpointFamily(options.requestedPreset)

  // Ordering matters because downstream selection treats the first entry as the
  // literal user request and the rest as increasingly stronger fallbacks.
  const candidates = [
    createCandidate({
      sourceSeed: 'requested',
      sourcePreset: options.requestedPreset,
      selectedFamily: requestedFamily,
      directionLabel: options.requestedPreset,
      direction: VIEWPOINT_PRESETS[options.requestedPreset].position,
      requestedDirection: requestedDirection,
      targetBounds: options.targetBounds,
      explicitDistance: options.explicitDistance,
      targetY: options.targetY,
      distancePreset: options.requestedPreset,
    }),
    ...buildMicroYawCandidates(options, requestedDirection),
    buildSameFamilyNudgeCandidate(options, requestedDirection),
    ...buildSameFamilyObliqueCandidates(options, requestedDirection),
    buildSameFamilyLiftCandidate(options, requestedDirection),
    ...buildOrthogonalCandidates(options, requestedDirection),
    buildOutwardCandidate(options, requestedDirection),
    buildOppositeCandidate(options, requestedDirection),
    requestedFamily === 'top' ? null : buildTopCandidate(options, requestedDirection),
  ].filter(function (candidate): candidate is ViewCandidate {
    return Boolean(candidate)
  })

  return dedupeCandidates(candidates)
}
